import ActionDialog from "@dashboard/components/ActionDialog";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton";
import { DetailPageLayout } from "@dashboard/components/Layouts";
import {
  useWsmDealerGroupDeleteMutation,
  useWsmDealerGroupDetailsQuery,
  useWsmDealerGroupUpdateMutation,
  useWsmTierPriceBulkCreateMutation,
  useWsmTierPriceBulkDeleteMutation,
  useWsmTierPriceListQuery,
  useWsmTierPriceUpdateMutation,
  WsmErrorFragment,
  WsmTierPriceFragment,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import useNotifier from "@dashboard/hooks/useNotifier";
import { commonMessages } from "@dashboard/intl";
import { getMutationState } from "@dashboard/misc";
import { mapEdgesToItems } from "@dashboard/utils/maps";
import { Skeleton } from "@saleor/macaw-ui-next";
import React, { useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";

import WsmDealerGroupDetailsPage from "../components/WsmDealerGroupDetailsPage/WsmDealerGroupDetailsPage";
import { getWsmIndexedErrors } from "../errors";
import { sectionMessages } from "../messages";
import { WsmDealerGroupFormData } from "../types";
import { wsmDealerGroupListPath } from "../urls";
import {
  diffTierRows,
  getGroupLabel,
  getGroupUpdateInput,
  groupToFormData,
  splitDeletesByBlocking,
} from "../utils";

/** Saleor caps a page at 100 rows, and a live group runs to 304+ (contract). */
const TIER_PAGE = 100;
const TIER_MAX_PAGES = 10;

export const WsmDealerGroupDetailsView = ({ id }: { id: string }) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const notify = useNotifier();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const lastCursor = useRef<string | null>(null);
  const [errors, setErrors] = useState<WsmErrorFragment[]>([]);
  const [tierRowErrors, setTierRowErrors] = useState<Record<number, WsmErrorFragment>>({});
  const [saveState, setSaveState] = useState<ConfirmButtonTransitionState>("default");
  const { data, loading, refetch } = useWsmDealerGroupDetailsQuery({ variables: { id } });
  const {
    data: tierData,
    loading: tiersLoading,
    loadMore: loadMoreTiers,
    refetch: refetchTiers,
  } = useWsmTierPriceListQuery({ variables: { first: TIER_PAGE, filter: { group: id } } });
  const [updateGroup] = useWsmDealerGroupUpdateMutation();
  const [bulkCreateTiers] = useWsmTierPriceBulkCreateMutation();
  const [bulkDeleteTiers] = useWsmTierPriceBulkDeleteMutation();
  const [updateTier] = useWsmTierPriceUpdateMutation();
  const [deleteGroup, deleteGroupOpts] = useWsmDealerGroupDeleteMutation({
    onCompleted: result => {
      if (!result.wsmDealerGroupDelete?.errors.length) {
        notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
        navigate(wsmDealerGroupListPath);
      } else {
        setErrors(result.wsmDealerGroupDelete.errors);
        setDeleteOpen(false);
      }
    },
  });
  const tierConnection = tierData?.wsmTierPrices;
  const tiers = tierConnection ? ((mapEdgesToItems(tierConnection) ?? []) as WsmTierPriceFragment[]) : null;
  const hasMoreTiers = !!tierConnection?.pageInfo?.hasNextPage;
  const underCap = (tiers?.length ?? 0) < TIER_PAGE * TIER_MAX_PAGES;

  /**
   * One page a call because 100 rows is the server's cap, accumulated so the
   * whole price list is one grid and one save: a live group runs to 304+ rows
   * and a merchant editing a price list is editing all of it. Past the cap they
   * are told what they are looking at rather than shown a silently short list.
   */
  useEffect(() => {
    if (!hasMoreTiers || tiersLoading || !underCap) {
      return;
    }

    const after = tierConnection?.pageInfo?.endCursor;

    if (!after || after === lastCursor.current) {
      return;
    }

    lastCursor.current = after;
    loadMoreTiers(
      (previous, next) =>
        next.wsmTierPrices
          ? {
              ...next,
              wsmTierPrices: {
                ...next.wsmTierPrices,
                edges: [
                  ...(previous.wsmTierPrices?.edges ?? []),
                  ...next.wsmTierPrices.edges,
                ],
              },
            }
          : previous,
      { after },
    );
  }, [hasMoreTiers, tiersLoading, underCap, tierConnection, loadMoreTiers]);

  const group = data?.wsmDealerGroup;
  const handleSubmit = async (form: WsmDealerGroupFormData) => {
    if (!group || !tiers) {
      return;
    }

    setSaveState("loading");
    setErrors([]);
    setTierRowErrors({});

    const originalRows = groupToFormData(group, tiers).rows;
    const changes = diffTierRows(originalRows, form.rows, group.id);
    // Only the deletes a create is waiting on happen before that create. The rest
    // go last: the contract has no "replace this group's prices" mutation, so the
    // one property a fixed order CAN hold is that a refused create never costs
    // the merchant rows that were already deleted for it.
    const { blocking, trailing } = splitDeletesByBlocking(originalRows, changes);
    const collected: WsmErrorFragment[] = [];
    const groupResult = await updateGroup({
      variables: { id: group.id, input: getGroupUpdateInput(form) },
    });

    collected.push(...(groupResult.data?.wsmDealerGroupUpdate?.errors ?? []));

    if (!collected.length && blocking.length) {
      const result = await bulkDeleteTiers({ variables: { ids: blocking } });

      collected.push(...(result.data?.wsmTierPriceBulkDelete?.errors ?? []));
    }

    if (!collected.length) {
      for (const update of changes.updates) {
        const result = await updateTier({ variables: update });

        collected.push(...(result.data?.wsmTierPriceUpdate?.errors ?? []));
      }
    }

    if (!collected.length && changes.creates.length) {
      const result = await bulkCreateTiers({ variables: { tierPrices: changes.creates } });
      const createErrors = result.data?.wsmTierPriceBulkCreate?.errors ?? [];
      const indexed = getWsmIndexedErrors(createErrors, "tierPrices");

      // The payload numbers a refusal by its place in the CREATE list; the grid
      // knows rows by their place on screen. Without this remap a 300-row paste
      // highlights the wrong line.
      setTierRowErrors(
        Object.entries(indexed).reduce<Record<number, WsmErrorFragment>>(
          (acc, [createIndex, error]) => {
            const rowIndex = changes.createRowIndexes[Number(createIndex)];

            if (rowIndex !== undefined) {
              acc[rowIndex] = error;
            }

            return acc;
          },
          {},
        ),
      );
      collected.push(...createErrors);
    }

    if (!collected.length && trailing.length) {
      const result = await bulkDeleteTiers({ variables: { ids: trailing } });

      collected.push(...(result.data?.wsmTierPriceBulkDelete?.errors ?? []));
    }

    setErrors(collected);
    setSaveState(collected.length ? "error" : "success");

    if (!collected.length) {
      notify({ status: "success", text: intl.formatMessage(commonMessages.savedChanges) });
    }

    // Refetched on the refused path too, not just the happy one. A save is
    // several mutations and a later one can be refused after earlier ones have
    // committed; re-reading means the NEXT save is diffed against what the server
    // actually holds instead of against a picture taken before this attempt.
    // The grid keeps the merchant's draft either way - the page holds its own
    // form state and is not re-keyed here - and diffTierRows matches a draft row
    // to the server row that now occupies its slot, so the rows that did go
    // through come out of that diff as no-ops rather than as duplicate creates.
    lastCursor.current = null;
    await Promise.all([refetch(), refetchTiers()]);
  };

  const truncatedAt = hasMoreTiers && !underCap ? (tiers?.length ?? 0) : null;
  // Still paging counts as loading: the form takes its rows once, and a grid
  // that re-mounted on every page would throw away the merchant's edits.
  const paging = hasMoreTiers && underCap;

  if (loading || tiersLoading || paging || !group || !tiers) {
    return (
      <DetailPageLayout>
        <TopNav
          href={wsmDealerGroupListPath}
          title={intl.formatMessage(sectionMessages.groupListTitle)}
        />
        <DetailPageLayout.Content>
          <Skeleton />
        </DetailPageLayout.Content>
      </DetailPageLayout>
    );
  }

  return (
    <>
      <WsmDealerGroupDetailsPage
        key={group.id}
        title={getGroupLabel(group)}
        initialData={groupToFormData(group, tiers)}
        errors={errors}
        tierRowErrors={tierRowErrors}
        disabled={saveState === "loading"}
        saveButtonBarState={saveState}
        currencyCode={tiers[0]?.currencyCode ?? null}
        truncatedAt={truncatedAt}
        showTierPrices
        groupId={group.id}
        customerCount={group.customerCount}
        tierPriceCount={group.tierPriceCount}
        onSubmit={handleSubmit}
        onDelete={() => setDeleteOpen(true)}
      />
      <ActionDialog
        open={deleteOpen}
        variant="delete"
        title={intl.formatMessage(sectionMessages.groupDeleteTitle)}
        confirmButtonState={getMutationState(
          deleteGroupOpts.called,
          deleteGroupOpts.loading,
          [],
        )}
        onClose={() => setDeleteOpen(false)}
        onConfirm={() => deleteGroup({ variables: { id: group.id } })}
      >
        {intl.formatMessage(sectionMessages.groupDeleteContent, {
          name: getGroupLabel(group),
          count: group.tierPriceCount,
        })}
      </ActionDialog>
    </>
  );
};

export default WsmDealerGroupDetailsView;
